import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Plus, Search, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';

export default function ClaimListPage() {
  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [participants, setParticipants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [genDialog, setGenDialog] = useState(false);
  const [genForm, setGenForm] = useState({ branch_id: '', date_from: '', date_to: '', participant_id: '', grouping_mode: 'BY_DATE_RANGE', payer_type: '' });
  const [genLoading, setGenLoading] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ page, pageSize: 20, sortBy: 'created_at', sortDir: 'desc' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const [clmRes, pRes, bRes] = await Promise.all([
        api.get(`/claims?${params}`), api.get('/participants?pageSize=100'), api.get('/org/branches'),
      ]);
      setClaims(clmRes.data.items || clmRes.data.data || []);
      setTotal(clmRes.data.total || 0);
      setParticipants(pRes.data.items || pRes.data.data || []);
      setBranches(bRes.data.data || []);
    } catch (e) { notify.error(e, 'CLAIM_LIST_LOAD_FAILED'); }
  };
  useEffect(() => { fetchData(); }, [page, statusFilter]); // eslint-disable-line

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const body = { date_from: genForm.date_from, date_to: genForm.date_to, grouping_mode: genForm.grouping_mode };
      if (genForm.branch_id) body.branch_id = genForm.branch_id;
      if (genForm.participant_id) body.participant_id = genForm.participant_id;
      if (genForm.payer_type) body.payer_type = genForm.payer_type;
      const res = await api.post('/claims/generate', body);
      toast.success(`Claim ${res.data.data.claim_no} generated`);
      setGenDialog(false); fetchData();
      navigate(`/finance/claims/${res.data.data.id}`);
    } catch (e) { notify.error(e, 'CLAIM_GENERATE_FAILED'); }
    finally { setGenLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">Claim Batches</h1><p className="text-muted-foreground mt-1">NDIS claim management</p></div>
        <Dialog open={genDialog} onOpenChange={setGenDialog}>
          <DialogTrigger asChild><Button data-testid="generate-claim"><Plus className="h-4 w-4 mr-2" /> Generate Claim</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Generate Claim Batch</DialogTitle><DialogDescription>Select approved timesheets to include</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date From</Label><Input type="date" value={genForm.date_from} onChange={(e) => setGenForm({...genForm, date_from: e.target.value})} required /></div>
                <div className="space-y-2"><Label>Date To</Label><Input type="date" value={genForm.date_to} onChange={(e) => setGenForm({...genForm, date_to: e.target.value})} required /></div>
              </div>
              <div className="space-y-2"><Label>Branch (optional)</Label>
                <Select value={genForm.branch_id || '__all'} onValueChange={(v) => setGenForm({...genForm, branch_id: v === '__all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent><SelectItem value="__all">All branches</SelectItem>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Participant (optional)</Label>
                <Select value={genForm.participant_id || '__all'} onValueChange={(v) => setGenForm({...genForm, participant_id: v === '__all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All participants" /></SelectTrigger>
                  <SelectContent><SelectItem value="__all">All participants</SelectItem>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Grouping</Label>
                  <Select value={genForm.grouping_mode} onValueChange={(v) => setGenForm({...genForm, grouping_mode: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BY_DATE_RANGE">By Date Range</SelectItem>
                      <SelectItem value="BY_PARTICIPANT">By Participant</SelectItem>
                      <SelectItem value="BY_BRANCH">By Branch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Payer Type (optional)</Label>
                  <Select value={genForm.payer_type || '__none'} onValueChange={(v) => setGenForm({...genForm, payer_type: v === '__none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Any</SelectItem>
                      <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
                      <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
                      <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
                      <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full" onClick={handleGenerate} disabled={genLoading || !genForm.date_from || !genForm.date_to} data-testid="generate-claim-submit">
                {genLoading ? 'Generating...' : 'Generate Claim Batch'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {['DRAFT','READY','SUBMITTED','PAID','PART_PAID','REJECTED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} claim(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Claim No</TableHead><TableHead>Period</TableHead><TableHead>Branch</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {claims.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No claims</TableCell></TableRow>
              : claims.map(c => (
                <TableRow key={c.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/finance/claims/${c.id}`)}>
                  <TableCell className="font-mono text-xs">{c.claim_no}</TableCell>
                  <TableCell className="text-xs">{c.date_from} to {c.date_to}</TableCell>
                  <TableCell>{c.branch_id || 'All'}</TableCell>
                  <TableCell className="tabular-nums">{c.totals?.count || c.line_items?.length || 0}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">${(c.totals?.amount || 0).toFixed(2)}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
